import { IMasterSlotsResponse, IRemotePerDayResponse, IRemotePerTimeResponse, IShopPerDayResponse, IShopPerTimeResponse } from './../../shared/models/slot-management.model';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';


import * as fromActions from './sales-availability.actions';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { SlotService } from '../../shared/services/slot.service';
import { AlertService } from '../../shared/services/alert.service';
import { TransportationType } from '@signal/asp-data-commons';
import { enrichShopMaxSlots } from './sales-availability.adapter';

@Injectable()
export class SalesAvailabilityEffects {
    availabilityType = 'sales';
    slotDetailsUpdatedSuccessfully = 'otherTexts.slotDetailsUpdatedSuccessfully';
    somethingWentWrongPleaseTryAgain = 'otherTexts.somethingWentWrongPleaseTryAgain';

    
    loadMasterSlots: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadMasterSlots>(
            fromActions.SalesAvailabilityActionTypes.LOAD_MASTER_SLOTS
        ),
        mergeMap((action: fromActions.LoadMasterSlots) =>
            this.slotService.getMasterSlots(this.availabilityType).pipe(
                map((masterSlots: IMasterSlotsResponse) => new fromActions.LoadMasterSlotsSuccess({ masterSlots: masterSlots.data })),
                catchError(err => of(new fromActions.LoadMasterSlotsFailure({ error: err })))
            )
        )
    ));

    
    loadShopMaxTotal: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadShopMaxTotal>(
            fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_TOTAL
        ),
        mergeMap((action: fromActions.LoadShopMaxTotal) =>
            this.slotService.getShopMaxTotal(this.availabilityType).pipe(
                map((response: IShopPerDayResponse) => new fromActions.LoadShopMaxTotalSuccess({ shopMaxTotal: response.data })),
                catchError(err => of(new fromActions.LoadShopMaxTotalFailure({ error: err })))
            )
        )
    ));

    
    loadShopMaxSlots: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadShopMaxSlots>(
            fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_SLOTS
        ),
        mergeMap((action: fromActions.LoadShopMaxSlots) =>
            this.slotService.getShopMaxSlots(this.availabilityType).pipe(
                map((shopMaxSlots: IShopPerTimeResponse) => 
                new fromActions.LoadShopMaxSlotsSuccess(
                    { shopMaxSlots: enrichShopMaxSlots(shopMaxSlots.data) })),
                catchError(err => of(new fromActions.LoadShopMaxSlotsFailure({ error: err })))
            )
        )
    ));

    
    updateShopMaxTotal: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateShopMaxTotal>(
            fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_TOTAL
        ),
        mergeMap((action: fromActions.UpdateShopMaxTotal) =>
            this.slotService.updateShopMaxTotal(action.payload.shopMaxTotal, this.availabilityType).pipe(
                map((response: IShopPerDayResponse) => {
                    this.alertService.showAlert(this.slotDetailsUpdatedSuccessfully, '', '', 5000, false);
                    return new fromActions.UpdateShopMaxTotalSuccess({ shopMaxTotal: response.data })
                }),
                catchError(err => {
                    this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
                    return of(new fromActions.UpdateShopMaxTotalFailure({ error: err }))
                })
            )
        )
    ));

    
    updateShopMaxSlots: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateShopMaxSlots>(
            fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_SLOTS
        ),
        mergeMap((action: fromActions.UpdateShopMaxSlots) =>
            this.slotService.updateShopMaxSlots(action.payload.shopMaxSlots, this.availabilityType).pipe(
                map((response: IShopPerTimeResponse) => {
                    if(!action.payload.bothFormsEdited)
                    {
                        this.alertService.showAlert(this.slotDetailsUpdatedSuccessfully, '', '', 5000, false);
                    }
                    return new fromActions.UpdateShopMaxSlotsSuccess({ shopMaxSlots: response.data })
                }),
                catchError(err => {
                    if(!action.payload.bothFormsEdited)
                    {
                        this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
                    }
                    return of(new fromActions.UpdateShopMaxSlotsFailure({ error: err }))
                })
            )
        )
    ));

    
    loadTransportMaxTotal: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadTransportMaxTotal>(
            fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_TOTAL
        ),
        mergeMap((action: fromActions.LoadTransportMaxTotal) =>
            this.slotService.getTransportMaxTotal(action.payload.transport, this.availabilityType).pipe(
                map((response: IRemotePerDayResponse) => new fromActions.LoadTransportMaxTotalSuccess(
                    { transport: action.payload.transport ,transportMaxTotal: response.data })),
                catchError(err => of(new fromActions.LoadTransportMaxTotalFailure({ error: err })))
            )
        )
    ));

    
    loadTransportMaxSlots: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadTransportMaxSlots>(
            fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_SLOTS
        ),
        mergeMap((action: fromActions.LoadTransportMaxSlots) =>
            this.slotService.getTransportMaxSlots(action.payload.transport, this.availabilityType).pipe(
                map((response: IRemotePerTimeResponse) => new fromActions.LoadTransportMaxSlotsSuccess(
                    { transport: action.payload.transport, transportMaxSlots: enrichShopMaxSlots(response.data) })),
                catchError(err => of(new fromActions.LoadTransportMaxSlotsFailure({ error: err })))
            )
        )
    ));

    
    updateTransportMaxTotal: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateTransportMaxTotal>(
            fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_TOTAL
        ),
        mergeMap((action: fromActions.UpdateTransportMaxTotal) =>
            this.slotService.updateTransportMaxTotal(action.payload.transport, action.payload.transportMaxTotal, this.availabilityType).pipe(
                map((response: IRemotePerDayResponse) => {
                    this.alertService.showAlert(this.slotDetailsUpdatedSuccessfully, '', '', 5000, false);
                    return new fromActions.UpdateTransportMaxTotalSuccess(
                        { transport: action.payload.transport, transportMaxTotal: response.data })
                }),
                catchError(err => {
                    this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
                    return of(new fromActions.UpdateTransportMaxTotalFailure({ error: err }))
                })
            )
        )
    ));

    
    updateTransportMaxSlots: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateTransportMaxSlots>(
            fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_SLOTS
        ),
        mergeMap((action: fromActions.UpdateTransportMaxSlots) =>
            this.slotService.updateTransportMaxSlots(action.payload.transport, action.payload.transportMaxSlots, this.availabilityType).pipe(
                map((response: IRemotePerTimeResponse) => {
                    if(!action.payload.bothFormsEdited)
                    {
                        this.alertService.showAlert(this.slotDetailsUpdatedSuccessfully, '', '', 5000, false);
                    }
                    return new fromActions.UpdateTransportMaxSlotsSuccess(
                        { transport: action.payload.transport, transportMaxSlots: response.data })
                }),
                catchError(err => {
                    if(!action.payload.bothFormsEdited)
                    {
                        this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
                    }
                    return of(new fromActions.UpdateTransportMaxSlotsFailure({ error: err }))
                })
            )
        )
    ));

    constructor(
        private actions$: Actions,
        private slotService: SlotService,
        private alertService: AlertService
    ) {
    }
}